export const fr = {
    seo: {
        title: 'MyCarCosts | Ultimate Car Expense Tracker & Repair Analysis',
        description: 'Track car maintenance, expenses, and history. Use our AI-powered repair vs sell analyzer to make data-driven decisions about your vehicle.'
    },
    common: {
        back: "Retour",
        save: "Enregistrer",
        saved: "Enregistré",
        cancel: "Annuler",
        close: "Fermer",
        accessDenied: "Accès refusé",
        noExportPermission: "Vous n'avez pas la permission d'exporter ces données de voiture.",
        delete: "Supprimer",
        downgrade: "Rétrograder",
        downgradeConfirm: "Êtes-vous sûr de vouloir rétrograder vers le plan gratuit ?",
        edit: "Modifier",
        loading: "Chargement...",
        confirm: "Confirmer",
        processing: "Traitement...",
        history: "Historique",
        currentCar: "Voiture actuelle",
        purchasedCarTitle: "Achat de voiture",
        initialCost: "Coût initial",
        done: "Fait",
        ok: "OK",
        error: "Erreur",
        errorSaving: "Erreur lors de l'enregistrement des données",
        mandatoryFieldsError: "Veuillez remplir tous les champs obligatoires correctement.",
        search: "Rechercher",
        milesShort: "mi",
        kmShort: "km",
        invalidEmail: "Format d'email invalide",
        today: "Aujourd'hui",
        car: "Voiture",
        all: "Tout",
        deleteConfirm: "Supprimer",
        unsavedChangesTitle: "Modifications non enregistrées",
        unsavedChangesMessage: "Voulez-vous enregistrer les modifications avant de quitter ?",
        dontSave: "Ne pas enregistrer",
        additionalOptions: "Options supplémentaires",
        add: "Ajouter",
        fileTooLarge: "Le fichier est trop volumineux. La taille maximale est de {limit} Mo.",
        storageFull: "Limite de stockage dépassée. Le stockage maximum est de {limit} Mo.",
        dailyUploadLimit: "Limite de téléchargement quotidienne atteinte ({limit} fichiers/jour). Réessayez demain.",
        hourlyUploadLimit: "Limite de téléchargement horaire atteinte ({limit} fichiers/heure). Réessayez dans une heure.",
        fileCountLimit: "Maximum de {limit} fichiers autorisés par élément.",
        emailParseLimitReached: "Limite de traitement des emails atteinte. Veuillez mettre à niveau votre plan ou traiter manuellement.",
        emailLimitText: "Limite de traitement des emails atteinte.",
        monthlyEmailScans: "Analyses d'emails mensuelles",
        lifetimeEmailScans: "Analyses d'emails à vie",
        monthlyFileScans: "Analyses de reçus mensuelles",
        lifetimeFileScans: "Analyses de reçus à vie",
        fileLimitText: "Limite d'analyse de reçus atteinte.",
        planFreeTier: "Niveau gratuit",
        planProTier: "Niveau pro",
        uploadFailed: "Échec du téléchargement",
        attachments: "Pièces jointes",
        limitsAndUsage: "Limites et utilisation",
        addresses: "adresses",
        emailCopied: "Adresse email copiée !",
        units: {
            liters: "l",
            gallons: "gal",
            kilometers: "km",
            miles: "mi",
        },
        included: "Inclus",
        proOnly: "PRO uniquement",
        month: "mois",
        openInNewTab: "Ouvrir dans un nouvel onglet",
        menu: "Menu",
        home: "Accueil",
        monthsShort: {
            0: "Janv",
            1: "Fév",
            2: "Mars",
            3: "Avr",
            4: "Mai",
            5: "Juin",
            6: "Juil",
            7: "Août",
            8: "Sept",
            9: "Oct",
            10: "Nov",
            11: "Déc",
        },
    },
    carMetadata: {
        bodyType: "Type de carrosserie",
        drivetrain: "Transmission",
        transmission: "Boîte de vitesses",
        bodyTypes: {
            sedan: "Berline",
            hatchback: "Hatchback",
            wagon: "Break",
            suv: "SUV",
            crossover: "Crossover",
            coupe: "Coupé",
            convertible: "Cabriolet",
            pickup: "Pickup",
            van: "Fourgon / Minivan",
            commercial: "Commercial",
            other: "Autre",
            not_specified: "-",
        },
        drivetrains: {
            not_specified: "-",
            fwd: "Traction avant",
            rwd: "Traction arrière",
            awd: "AWD / 4x4",
            other: "Autre",
        },
        transmissions: {
            not_specified: "-",
            manual: "Manuelle",
            automatic: "Automatique",
            cvt: "CVT",
            other: "Autre",
        },
        otherLabel: "Autre (préciser)",
        mileageLabel: "Kilométrage",
    },
    auth: {
        welcome: "Bienvenue",
        welcomeBack: "Bon retour",
        signIn: "Se connecter",
        signInWithGoogle: "Se connecter avec Google",
        signOut: "Se déconnecter",
        magicLinkDesc: "Recevez un lien magique dans votre email",
        magicLinkSent: "Lien magique envoyé !",
        magicLinkCheckEmail: "Vérifiez votre email et cliquez sur le lien pour vous connecter.",
        emailAddress: "Adresse email",
        getMagicLink: "Obtenir le lien magique",
        sending: "Envoi...",
        linkSent: "Lien envoyé",
        orContinueWith: "Ou continuez avec",
        googleAccount: "Compte Google",
        newUserNote: "Nouveau ici ? Votre compte sera créé automatiquement lors de votre première connexion réussie.",
        magicLinkError: "Échec de l'envoi du lien magique. Veuillez vérifier votre email et réessayer.",
        genericError: "Une erreur est survenue. Veuillez réessayer.",
        projectDescription: "Suivez vos coûts de voiture, maintenance et historique en un seul endroit.",
        loginError: "Impossible de se connecter",
        loggingIn: "Connexion en cours...",
        authenticating: "Authentification...",
        authenticatingDesc: "Veuillez patienter pendant que nous sécurisons votre session.",
    },
    settings: {
        title: "Paramètres",
        systemPreferences: "Préférences système",
        appSettings: "Paramètres de l'application",
        dangerZone: "Zone de danger",
        theme: "Thème",
        language: "Langue de l'interface",
        currency: "Monnaie",
        units: "Unités",
        distanceUnit: "Unité de distance",
        volumeUnit: "Unité de volume",
        expenseTypes: "Types de dépenses",
        expenseTypesDesc: "Gérer les catégories",
        categories: "Catégories",
        exportData: "Exporter les données",
        exportDesc: "Télécharger le CSV complet",
        importData: "Importer les données",
        importDesc: "Restaurer à partir d'une sauvegarde CSV",
        deleteAccount: "Supprimer le compte",
        deleteAccountDesc: "Supprimer définitivement toutes les données",
        enableSync: "Activer la synchronisation",
        enableSyncDesc: "Synchroniser les données sur les appareils",
        account: "Compte",
        accountPlan: "Plan de compte",
        carsUsage: "Voitures",
        carsLimitReached: "Limite de voitures atteinte",
        storageSize: "Taille de stockage",
        emailScans: "Scans par e-mail",
        fileScans: "Scans de reçus",
        plans: "Plans",
        currentlyPlan: "Actuellement",
        upgradePlan: "Mettre à niveau",
        manageSubscription: "Gérer l'abonnement",
        nextPayment: "Prochain paiement",
        endsOn: "Se termine le",
        adminConsole: "Console d'administration",
        systemControl: "Contrôle du système",
        customCategories: "Catégories et étiquettes personnalisées",
        interfaceTheme: "Thème de l'interface",
        light: "Clair",
        dark: "Sombre",
        timezone: "Fuseau horaire",
        timezoneDesc: "Utilisé pour les rappels par e-mail programmés",
        timeFormat: "Format de l'heure",
        timeFormat24h: "24 heures (14:30)",
        timeFormat12h: "12 heures (2:30 PM)",
        kilometers: "kilomètres (km)",
        miles: "miles (mi)",
        liters: "litres (l)",
        gallons: "gallons (gal)",
        accountManagement: "Gestion du compte",
        contactUs: "Contactez-nous",
        contactDesc: "Pour suggestions, demandes de fonctionnalités et questions",
        logOut: "Se déconnecter",
        logOutConfirmTitle: "Se déconnecter",
        logOutConfirmMessage: "Êtes-vous sûr de vouloir vous déconnecter de votre compte ?",
        keepAccount: "Conserver le compte",
        deleteAccountConfirmTitle: "Supprimer le compte",
        deleteAccountConfirmMessage: "Êtes-vous sûr de vouloir supprimer définitivement votre compte et toutes les données associées à la voiture ? Cette action ne peut pas être annulée.",
        deletePermanently: "Supprimer définitivement",
        deleting: "Suppression...",
        contactSupport: "Contacter le support",
        messageSent: "Message envoyé !",
        feedbackThanks: "Merci pour vos retours. Nous apprécions votre contribution et allons l'examiner.",
        backToSettings: "Retour aux paramètres",
        subjectLabel: "Objet",
        messageLabel: "Message",
        subjectPlaceholder: "De quoi s'agit-il ?",
        messagePlaceholder: "Parlez-nous de votre suggestion, demande de fonctionnalité ou question...",
        enterSubjectAlert: "Veuillez entrer un objet",
        enterMessageAlert: "Veuillez entrer un message",
        sendFailedAlert: "Échec de l'envoi du message. Veuillez réessayer plus tard.",
    },
    export: {
        title: "Exporter les données",
        generatePDF: "Générer le PDF",
        carInformation: "Informations sur la voiture",
        editInfo: "Modifiez les informations ci-dessous pour personnaliser votre export",
        editInfoHint: "Vous pouvez supprimer ou modifier tous les champs que vous ne souhaitez pas inclure",
        filters: "Filtres",
        expenseTypes: "Types de dépenses",
        includeNotes: "Inclure les notes",
        includeImages: "Inclure les fichiers",
        hideDisabled: "Masquer les éléments désactivés",
        itemsCount: "{total} éléments, {selected} sélectionnés",
        selectAll: "Tout sélectionner",
        deselectAll: "Désélectionner tout",
        expenses: "Dépenses",
        notes: "Notes",
        spareParts: "Pièces de rechange",
        images: "Images",
        files: "Fichiers",
        summary: "Résumé",
        totalExpenses: "Dépenses totales",
        totalItems: "Éléments totaux",
        generatedOn: "Généré le",
        noItemsSelected: "Aucun élément sélectionné pour l'export",
        noItemsAvailable: "Aucun élément disponible à exporter",
        generating: "Génération du PDF...",
        downloadPDF: "Télécharger le PDF",
        pdfFileName: "{carName}_export_{date}.pdf",
        reportTitle: "Rapport de dépenses de voiture",
        exportButton: "Exporter",
        generateCSV: "Générer CSV",
        formatSelectorTitle: "Choisir le format",
        formatSelectorMsg: "Veuillez choisir le format d'exportation",
        proFeatureExportTitle: "Fonctionnalité Pro : Exporter des données",
        proFeatureExportDesc: "Passez à PRO pour exporter l'historique de votre voiture en PDF.",
        exportRestrictedPopup: "L'exportation des données est uniquement disponible pour les utilisateurs Pro.",
        allTypes: "Tous les types",
        statistics: "Statistiques",
        driven: "Conduit",
        since: "Depuis",
        averageCost: "Coût moyen",
        perKm: "par km",
        perMile: "par mile",
        period: "Période",
        dateFrom: "De",
        dateTo: "À",
        allTime: "Effacer le filtre",
        periodHint: "Si aucune période n'est sélectionnée — toutes les dépenses sont affichées",
        noExpenses: "Aucune dépense ne correspond aux filtres",
        expensesSection: "Dépenses",
        attachmentsSection: "Pièces jointes",
        imagesCount: "{count} images",
        pdfsCount: "{count} PDFs",
        includeParts: "Inclure les pièces de rechange",
        pageLabel: "Page",
    },
    expenses: {
        title: "Dépenses",
        newExpense: "Nouvelle dépense",
        editExpense: "Modifier la dépense",
        openExpense: "Ouvrir l'entrée de dépense",
        details: "Détails de la dépense",
        selectType: "Sélectionner le type",
        selectTypePlaceholder: "Sélectionner le type de dépense...",
        shortDescriptionPlaceholder: "Description courte...",
        expenseTitle: "Titre",
        logEntryTitleLabel: "Titre de l'enregistrement (Optionnel)",
        defaultTitlePlaceholder: "Titre par défaut - {type}",
        autoTitlePlaceholder: "Sera rempli automatiquement en fonction des types sélectionnés",
        carDataTitle: "Données de la voiture",
        defaultTitlePrefix: "Le titre par défaut sera -",
        addAnother: "Ajouter un autre type de dépense",
        addAnotherHint: "Si cet enregistrement comprend plusieurs types de dépenses, vous pouvez les ajouter tous ici pour garantir des statistiques précises.",
        carValueDescription: "Valeur marchande estimée de la voiture à ce moment.",
        type: "Type",
        date: "Date",
        amount: "Montant",
        totalAmount: "Total",
        splitCosts: "Partager les coûts ?",
        combineCosts: "Combiner les coûts",
        amountWork: "Travail",
        amountParts: "Pièces",
        mileage: "Kilométrage",
        carValue: "Valeur de la voiture",
        description: "Description",
        detailedDescription: "Description détaillée",
        electricity: "Électricité",
        fuelType: "Type de carburant",
        fuelTypeOptions: {
            petrol: "Essence",
            diesel: "Diesel",
            lpg: "LPG",
            electricity: "Électricité",
        },
        carData: "Données de la voiture",
        images: "Fichiers",
        addImages: "Ajouter des fichiers",
        addImagesPDFs: "Ajouter des images ou des PDFs",
        deleteExpense: "Supprimer la dépense",
        deleteConfirm: "Êtes-vous sûr de vouloir supprimer cette dépense ?",
        saveSuccess: "Dépense enregistrée avec succès",
        thisExpense: "Cette dépense",
        stats: {
            annual: "Annuel",
            allTime: "Tout le temps",
            avgMonth: "Moyenne/Mois",
            costPer: "Coût/",
            noExpenses: "Pas encore de dépenses",
            dragToReorder: "Faites glisser pour réorganiser",
        },
        expensesFound: "Créées",
        noExpensesFound: "Aucune dépense trouvée.",
        noDescription: "Pas de description",
        imagesCount: "Images ({count})",
        deleteImageTitle: "Supprimer l'image",
        deleteImageConfirm: "Êtes-vous sûr de vouloir supprimer cette image ? Cette action ne peut pas être annulée.",
        typeLabels: {
            fuel: "Carburant",
            car_wash: "Lavage",
            repairs: "Réparations",
            maintenance: "Entretien",
            parts: "Pièces de rechange",
            insurance: "Assurance",
            inspection: "Inspection",
            tyres: "Pneus",
            parking: "Stationnement",
            electricity: "Électricité",
            taxes: "Taxes",
            other: "Autre",
            info: "Information",
            service_maintenance: "Entretien",
            service_repair: "Réparation",
            service_tuning: "Tuning",
        },
        serviceGroup: {
            name: "Service",
            selectSubtype: "Sélectionner le type de service",
            maintenance: "Entretien",
            repair: "Réparation",
            tuning: "Tuning",
        },
    },
    repeatingExpenses: {
        title: "Dépense récurrente",
        repeatToggle: "Répéter cette dépense",
        repeatFrequency: "Fréquence",
        monthly: "Mensuel",
        startDate: "Date de début",
        nextDate: "La prochaine entrée sera générée",
        endDate: "Date de fin",
        noEndDate: "Pas de date de fin",
        copyOptions: "Options de copie",
        copyTitle: "Copier le titre de l'enregistrement",
        copyLocation: "Copier l'emplacement",
        copyDescription: "Copier la description",
        active: "Actif",
        status: "Statut",
        managedByRepeat: "Cette dépense fait partie d'une séquence répétée.",
        placeholder: "Ici, vous pouvez configurer la répétition automatique pour cette dépense si elle se produit régulièrement avec le même montant.",
        deleteRepeat: "Supprimer la répétition",
        deleteRepeatConfirm: "Êtes-vous sûr de vouloir arrêter et supprimer cette séquence répétée ? Les futures entrées ne seront pas générées. Les entrées passées resteront.",
        relatedEntries: "Entrées dans cette séquence",
        stopRepeat: "Arrêter de répéter",
        isRepeating: "Répétition",
        noTerm: "Indéfiniment",
        until: "La dernière entrée sera générée",
        summaryCopy: "Le système va copier",
        simulate: "Simuler la génération de la prochaine entrée",
        simulateSuccess: "Génération de la prochaine entrée simulée avec succès",
        simulateNoDate: "Plus de dates à générer (date de fin atteinte)",
    },
    onboarding: {
        title: "Configurer le profil",
        welcome: "Bienvenue !",
        subtitle: "Personnalisez votre expérience de conduite.",
        currency: "Monnaie",
        preferredCurrency: "Monnaie préférée",
        distUnit: "Unité de distance",
        volUnit: "Unité de volume",
        complete: "Terminer la configuration",
    },
    cars: {
        unitChangeWarningTitle: "Avertissement de changement d'unité",
        unitChangeWarningMessage: "Changer ces unités ne convertira pas vos données existantes. Tous les enregistrements historiques resteront exactement tels qu'entrés.",
        unitChangeWarningConfirm: "Compris",
        title: "Garage",
        carAdmins: "Administrateurs de voiture",
        sharedWithMe: "Partagé avec moi",
        manageAdmins: "Gérer les administrateurs",
        adminEmail: "Email de l'administrateur",
        addAdmin: "Ajouter un administrateur",
        addAdminPlaceholder: "ex. ami@email.com",
        removeAdmin: "Supprimer",
        removeAdminConfirm: "Êtes-vous sûr de vouloir supprimer cet administrateur ?",
        adminAdded: "Administrateur ajouté",
        adminRemoved: "Administrateur supprimé",
        removeMyAccess: "Supprimer mon accès",
        removeMyAccessConfirm: "Êtes-vous sûr de vouloir renoncer à l'accès à cette voiture ?",
        myAccessRemoved: "Accès supprimé",
        noAdminsYet: "Aucun administrateur ajouté pour le moment.",
        adminInfoText: "Les administrateurs peuvent ajouter et modifier des dépenses, des notes et des pièces de rechange, mais ne peuvent pas supprimer la voiture elle-même.",
        newCar: "Ajouter une voiture",
        carNotFound: "Voiture non trouvée",
        carNotFoundDesc: "La voiture demandée n'existe pas ou vous n'avez pas accès.",
        accessDenied: "Accès refusé",
        accessDeniedDesc: "Cette voiture est privée et non disponible publiquement.",
        backToHome: "Retour à l'accueil",
        adminRestricted: "Accès restreint",
        adminRestrictedDesc: "Seuls les administrateurs autorisés peuvent accéder à cette console. Veuillez revenir à l'application principale.",
        returnHome: "Retour à l'accueil",
        editCar: "Modifier la voiture",
        name: "Nom de la voiture",
        model: "Modèle",
        year: "Année",
        plate: "Numéro de plaque",
        vin: "Numéro VIN",
        mileage: "Kilométrage actuel",
        initialMileage: "Kilométrage initial",
        engineType: "Type de moteur",
        tapToChangeIcon: "Touchez pour changer l'icône",
        chooseIcon: "Choisir une icône",
        imageTab: "Image",
        iconTab: "Icône",
        chooseColor: "Choisir une couleur",
        chooseEmoji: "Choisir un emoji",
        uploadImage: "Télécharger une image",
        clickToBrowse: "Cliquez pour parcourir les fichiers",
        currentCarIcon: "Icône de la voiture actuelle",
        changeImage: "Changer l'image",
        zoom: "Zoom",
        rotate: "Faire pivoter",
        removeChangeImage: "Supprimer / Changer l'image",
        selectIcon: "Sélectionner une icône",
        cropAndSave: "Rogner et enregistrer",
        displayName: "Nom affiché",
        displayNamePlaceholder: "ex. Ma voiture de week-end",
        makeLabel: "Marque *",
        modelLabel: "Modèle *",
        yearLabel: "Année *",
        fuelTypeLabel: "Type *",
        licensePlateLabel: "Plaque d'immatriculation",
        vinLabel: "VIN",
        notesLabel: "Notes",
        notesPlaceholder: "ex. 1.6 TDI, 85kW, 1588cc, batterie 62kWh, AWD, etc.",
        publicAccess: "Rendre la voiture accessible au public",
        publicAccessDesc: "D'autres peuvent voir cette voiture via un lien partagé",
        shareLocations: "Autoriser à voir également les emplacements ajoutés",
        shareLocationsDesc: "Activez ceci pour rendre les emplacements de voiture visibles en vue publique",
        deleteCar: "Supprimer la voiture",
        deleteCarTitle: "Supprimer la voiture",
        deleteCarConfirm: "Êtes-vous sûr de vouloir supprimer '{name}' ? Cette action ne peut pas être annulée.",
        noCars: "Aucune voiture dans votre garage",
        addFirstCarNote: "Commencez votre parcours en ajoutant votre premier véhicule pour suivre les dépenses.",
        addVehicle: "Ajouter un véhicule",
        myVehicles: "Mes véhicules",
        favorites: "Favoris",
        deleteConfirm: "Êtes-vous sûr de vouloir supprimer cette voiture ? Toutes les dépenses associées seront supprimées.",
        deleteFailed: "Échec de la suppression de la voiture",
        saveFailed: "Échec de l'enregistrement des données de la voiture",
        adminActions: "Actions administratives",
        importCsv: "Importer CSV",
        clearData: "Effacer les données",
        distanceUnitLabel: "Unité de distance",
        volumeUnitLabel: "Unité de volume",
        currencyLabel: "Devise",
        boughtAt: "Date d'achat",
        purchasePrice: "Prix d'achat",
        purchaseMileage: "Kilométrage d'achat",
        createPurchaseRecord: "Créer automatiquement un enregistrement d'historique d'achat",
        makePlaceholder: "ex. BMW",
        modelPlaceholder: "ex. X5",
        platePlaceholder: "ex. AB-1234",
        vinPlaceholder: "VIN...",
        purchaseInfoNote: "Cette information créera un enregistrement initial 'Information' dans votre historique avec les données spécifiées.",
        availableTypes: "Types disponibles",
        dragToReorder: "Faites glisser pour réorganiser",
        requestTypeNote: "Si vous avez besoin d'un autre type - ",
        requestTypeAction: "faites-le nous savoir !",
        emailProcessingTitle: "Traitement des e-mails",
        emailInstructions: "Transférez vos reçus à l'adresse ci-dessous. Nous extrairons les données et créerons des dépenses automatiquement.",
        recentEmails: "E-mails reçus",
        viewAllEmails: "VOIR TOUS ({count})",
        noEmailsReceived: "Aucun e-mail reçu pour le moment.",
        analysisResult: "Résultat de l'analyse",
        vehicleInfo: "Informations sur le véhicule",
        preferences: "Préférences",
        purchaseHistory: "Historique des achats",
        emailProcessing: "Traitement des e-mails",
        publicAccessTitle: "Accès public",
        expenseTypesTitle: "Types de dépenses",
        adminActionsTitle: "Actions administratives",
        enterPurchaseDateAlert: "Veuillez entrer la date d'achat",
        allowedSenderEmails: "E-mails d'expéditeurs autorisés",
        allowedEmailsDesc: "Ajoutez les adresses e-mail autorisées à envoyer des reçus à cette voiture. Maximum 5 adresses.",
        noAllowedEmails: "Aucun e-mail autorisé ajouté.",
        emailAlreadyAdded: "Cet e-mail est déjà autorisé pour cette voiture.",
        maxAllowedEmailsReached: "Limite maximale d'e-mails autorisés atteinte.",
        addAllowedEmailPlaceholder: "Ajouter un e-mail autorisé...",
        ownerEmail: "Propriétaire",
        onlyOwner: "propriétaire uniquement",
        defaultAllowed: "Toujours autorisé",
        carLimitReachedTitle: "Limite de voitures atteinte",
        carLimitReachedMessage: "Votre plan {plan} permet {limit} voiture(s). Vous en avez déjà {current}. Allez dans les paramètres pour voir vos limites ou contactez-nous pour mettre à niveau.",
        carLimitGoToSettings: "Aller aux paramètres",
        archivedCars: "Archivé",
        archiveCar: "Archiver la voiture",
        unarchiveCar: "Désarchiver la voiture",
        sellDate: "Date de vente",
        sellPrice: "Prix de vente",
        sellMileage: "Kilométrage de vente",
        archiveConfirm: "Êtes-vous sûr de vouloir archiver cette voiture ?",
        archiveInstructions: "Archiver une voiture signifie qu'elle n'est plus à vous ou que vous ne l'entretenez plus. Veuillez fournir les détails de la vente pour les statistiques. Cela rendra la voiture en lecture seule. Les champs ci-dessous peuvent encore être modifiés par la suite.",
        archivedBadge: "Archivé",
        saleDetails: "Détails de la vente",
        saleDetailsNote: "Les détails de la vente sont utilisés pour les statistiques. Vous pouvez les mettre à jour si nécessaire.",
        archiveDetailsNotFillable: "Veuillez remplir tous les champs pour archiver la voiture",
        emailAddressWillBeAvailableAfterSave: "L'adresse e-mail sera disponible après avoir enregistré la voiture.",
        proFeatureAdminTitle: "Fonctionnalité Pro : Administrateurs de voiture",
        proFeatureAdminDesc: "Ajoutez d'autres utilisateurs qui peuvent voir et gérer ces données de voiture.",
        proFeatureEmailTitle: "Fonctionnalité Pro : Expéditeurs supplémentaires",
        proFeatureEmailDesc: "Ajoutez d'autres adresses e-mail autorisées à partir desquelles des reçus peuvent être envoyés à cette voiture.",
        estimatedMileage: "Kilométrage estimé",
        summaryFrequencyLabel: "Fréquence du résumé",
        summaryFrequencyOptions: {
            none: "Désactivé",
            monthly: "Mensuel",
            semiAnnually: "Semestriel",
            annually: "Annuel",
        },
        healthProfilePrompt: "Complétez votre profil de santé pour une analyse précise !",
    },
    fuelTypes: {
        not_specified: "-",
        petrol: "Essence",
        diesel: "Diesel",
        hybrid: "Hybride",
        electric: "Électrique",
        lpg: "LPG",
    },
    statistics: {
        mileageTrend: "Tendance du kilométrage",
        title: "Statistiques",
        allYears: "Toutes les années",
        totalSpent: "Total dépensé",
        spentIn: "Dépensé en {year}",
        perMonth: "par mois",
        overall: "global",
        vsPrev12m: "vs 12m préc.",
        rolling12m: "12m glissants",
        byType: "Par type",
        byMonth: "Par mois",
        byYear: "Par année",
        noData: "Aucune donnée pour cette année",
        mostExpensive: "Le plus cher",
        top: "Haut",
        untitled: "Sans titre",
        assetAnalysis: "Analyse de la valeur et des coûts des actifs",
        burnNote: "* Le 'Total Brûlé' inclut à la fois l'argent dépensé et la diminution estimée de la valeur de la voiture.",
        carMarketValue: "Valeur marchande de la voiture",
        cumulativeCosts: "Coûts cumulés",
        totalBurn: "Total Brûlé (Coûts + Amortissement)",
        entries: "entrées",
        avgPrice: "Prix moyen",
        consumption: "Consommation",
        price: "Prix",
        fuelPriceTrend: "Tendance des prix du carburant",
        spendingTrend: "Tendance des dépenses",
        costPerKmTrend: "Coût par 1 km",
        costPerMileTrend: "Coût par 1 mile",
        serviceTrendsTitle: "Tendances de service",
        expensesOnMap: "Dépenses sur la carte",
        visitsCountLabel: "visites",
        locationsSummary: "Résumé des lieux",
        locationsCountLabel: "lieux",
        noLocationsFoundTitle: "Aucun lieu pour le moment",
        noLocationsFoundDesc: "Ajoutez des lieux à vos enregistrements pour les voir sur la carte.",
        addMileageData: "Ajouter des données de kilométrage",
        missingMileageInfo: "Pas assez de données pour calculer le coût par distance. Ajoutez au moins 2 relevés de kilométrage pendant cette période (ou un avant cette période).",
        mileageModal: {
            title: "Ajouter un kilométrage manquant",
            description: "Pour calculer avec précision les coûts par distance pour {year}, nous devons connaître le kilométrage au début et à la fin de l'année.",
            startOfYear: "Début de l'année (1er janv.)",
            endOfYear: "Fin de l'année (31 déc.)",
            currentMileage: "Kilométrage actuel",
            placeholder: "Entrez le kilométrage...",
            save: "Enregistrer les données",
            foundNearby: "Trouvé à proximité ({date}) : {value} {unit}",
            useExisting: "Utiliser ceci",
        },
        proAnalysis: "Analyse Pro",
        detailedAnalysis: "Analyse détaillée",
        upgradeToSee: "Passez à la version supérieure pour voir →",
    },
    tabs: {
        dashboard: "Tableau de bord",
        expenses: "Dépenses",
        stats: "Statistiques",
        locations: "Lieux",
        gallery: "Fichiers",
        parts: "Pièces",
        notes: "Notes",
        repair: "Mode Réparation",
        'repair-switch': "Interrupteur de voiture",
    },
    repairAnalysis: {
        title: "Analyse de la décision de réparation",
        ownershipLength: "Durée de possession",
        currentValue: "Valeur actuelle",
        valueOutdated: "La valeur peut être obsolète",
        addValueToEnable: "Ajoutez la valeur actuelle de la voiture pour activer l'analyse",
        totalInvested: "Total investi",
        totalInvestedSub: "Prix d'achat + réparations",
        totalExcludesFuel: "exclut le carburant / l'électricité",
        fuelLabelFuelOnly: "Total carburant",
        fuelLabelEnergyOnly: "Total énergie",
        fuelLabelHybrid: "Total carburant & énergie",
        fuelTypeFuel: "carburant",
        fuelTypeEnergy: "électricité",
        fuelTypeHybrid: "carburant & électricité",
        fuelTotalLabel: "Total Carburant/Énergie :",
        projectionMode: "Mode de projection",
        projectionModes: {
            lastYear: "Basé sur l'année dernière",
            average: "Moyenne historique",
            trend: "Tendance (linéaire)",
        },
        brakeAssumption: "L'entretien inclut un cycle de frein estimé de 3 ans",
        repairBuffer: "Inclut un tampon de risque de réparation basé sur l'âge",
        yearLabel: "année",
        totalInvestedDesc: "Prix d'achat + toutes les réparations",
        repairToValue: "Réparation par rapport à la valeur (12 mois)",
        repairToValueExplain: "Réparations €{cost} par rapport à la valeur de la voiture €{value}. En dessous de 20% est sain ; au-dessus de 50% est à haut risque.",
        ownershipPressure: "Pression de propriété",
        ownershipPressureExplain: "Pression totale des coûts : réparations €{cost} + perte de valeur par rapport à €{value}. Au-dessus de 35% signale un stress financier.",
        investmentGap: "Écart d'investissement",
        investmentGapExplain: "Total investi €{invested} moins la valeur actuelle €{value}. Cela montre combien vous avez 'perdu' sur la propriété jusqu'à présent.",
        investmentGapDesc: "Total investi - valeur actuelle",
        repairExpenses12m: "Coût de réparation sur 12 mois",
        calculatedYearlyMileage: "Kilométrage annuel calculé : {km} km",
        calculatedYearlyRepairs: "Réparations annuelles calculées : {amount}",
        set: "Définir",
        plannedRepairCost: "Coût de réparation prévu",
        whatIfTitle: "Que se passerait-il si vous dépensiez {amount} maintenant ?",
        updatedScore: "Score mis à jour",
        updatedVerdict: "Verdict mis à jour",
        howIsItCalculated: "Comment cela est-il calculé ?",
        usedLifespan: "Utilisé : {usage}% de la durée de vie attendue",
        remainingLifespan: "Restant : ~{km} km",
        estimatedLifespan: "Durée de vie estimée : {km} km",
        lifecycle_stage_early: "Début de cycle de vie – faible risque de pannes majeures",
        lifecycle_stage_mid: "Milieu de cycle de vie – usure normale",
        lifecycle_stage_late: "Fin de cycle de vie – risque accru de réparations",
        lifecycle_stage_end: "Fin de la durée de vie attendue – risque élevé de réparations majeures",
        calculationExplanation: "L'analyse utilise le prix d'achat de votre véhicule, les coûts de réparation récents et la valeur actuelle estimée pour calculer un score de santé financière. Des coûts de réparation élevés par rapport à la valeur de la voiture ou une dépréciation rapide abaisseront le score.",
        confidenceLabel: "Confiance",
        confidenceLevel: {
            high: "Élevé",
            medium: "Moyen",
            low: "Faible",
        },
        mentalFactorsTitle: "Améliorez la précision avec votre expérience",
        mentalFactorsCTA: "Vérification rapide (30 sec)",
        mentalFactorsSurveyTitle: "Votre expérience",
        mentalFactorsSurveyDesc: "Répondez à 3 questions rapides pour personnaliser votre score.",
        q1: "Votre voiture répond-elle à vos besoins ?",
        q1Help: "Espace, confort, fonctionnalités",
        q2: "Aimez-vous conduire votre voiture ?",
        q2Help: "Visuel, confort, ressenti",
        q3: "Vous inquiétez-vous des pannes imprévues ?",
        q3Help: "Préoccupations de fiabilité",
        q4: "Cette voiture est-elle pour la famille et les longs trajets ?",
        q4Help: "Priorité à la sécurité et à la fiabilité",
        factorsTitle: "Facteurs de Score",
        factorsDesc: "Voyez ce qui impacte votre score",
        newCarComparison: "Comparaison de Voitures Neuves",
        newCarComparisonDesc: "Comparez votre coût mensuel actuel à l'achat d'une voiture neuve.",
        modeLeasing: "Location",
        modeFullPrice: "Prix Complet",
        downPayment: "Acompte",
        leaseYears: "Durée (Années)",
        monthlyPayment: "Paiement Mensuel",
        carPrice: "Prix Complet de la Voiture",
        ownershipYears: "Propriété Prévue (Années)",
        insuranceMonthly: "CASCO / Assurance Mensuel",
        currentMonthlyCost: "Coût Auto Actuel",
        newCarMonthlyCost: "Coût de la Nouvelle Voiture",
        newCarCheaper: "La nouvelle voiture est moins chère de : {amount} / mois",
        newCarMoreExpensive: "La nouvelle voiture est plus chère de : {amount} / mois",
        newCarSameCost: "Les coûts sont exactement les mêmes.",
        advancedSettings: "Paramètres Avancés",
        yearlyMaint: "Entretien Annuel (€)",
        tyres3Yrs: "Pneus / 3 ans (€)",
        depreciation5Yrs: "Amortissement (5 ans %)",
        newCarYear: "Année de la Nouvelle Voiture",
        cumulativeCosts: "Coûts Cumulés (Projection)",
        comparisonByYear: "Comparaison par Année",
        currentCarLabel: "Voiture Actuelle",
        newCarLabel: "Nouvelle Voiture",
        resaleValue: "Valeur de revente",
        startupCosts: "Coûts de démarrage (voiture d'occasion)",
        inspection: "Inspection pré-achat",
        initialMaintenance: "Entretien initial + huiles",
        registration: "Enregistrement / Frais",
        total: "Total",
        currentCarTitle: "Voiture actuelle",
        newCarTitle: "Voiture potentielle",
        upfrontInvestment: "Investissement initial",
        monthlyReality: "Coût mensuel réel",
        depreciationShort: "Amortissement",
        howCalculated: "Comment cela est-il calculé ?",
        deltaMonthly: "Différence mensuelle",
        deltaUpfront: "Différence initiale",
        surveyMeanings: {
            practical: {
                1: "Pas du tout",
                2: "Mauvaise adéquation",
                3: "Moyenne",
                4: "Bonne adéquation",
                5: "Parfaite adéquation",
            },
            joy: {
                1: "Frustrant",
                2: "Ennuyeux",
                3: "Moyenne",
                4: "Agréable",
                5: "Beaucoup",
            },
            worry: {
                1: "Pas inquiet",
                2: "Légèrement inquiet",
                3: "Inquiet",
                4: "Très inquiet",
                5: "Sûr que je vais tomber en panne à tout moment",
            },
        },
        verdicts: {
            KEEP: "Financièrement raisonnable de garder",
            borderline: "Limite – surveillez de près",
            sell: "Risque financier élevé – vendre peut être rationnel",
        },
        factors: {
            trendInc: "Tendance à la Hausse",
            trendIncDesc: "Les coûts de réparation augmentent par rapport à l'année dernière.",
            lowDepreciation: "Avantage de Faible Amortissement",
            lowDepreciationDesc: "Les voitures plus anciennes/moins chères perdent moins de valeur avec le temps.",
            stableRepairs: "Réparations Stables",
            stableRepairsDesc: "Vos coûts de réparation sont stables ou en diminution.",
            noHistoricalData: "Aucune donnée de plus de 6 mois.",
            lifecycle_stage: "Phase du cycle de vie du véhicule",
            high_risk_phase: "Phase à Haut Risque",
            high_risk_phase_desc: "La combinaison d'une phase tardive du cycle de vie et d'une augmentation des coûts de réparation indique un risque élevé.",
            lowCostKm: "Coût Efficace par KM",
            lowCostKmDesc: "L'efficacité globale de l'utilisation de cette voiture est excellente.",
            lowFixedCosts: "Coûts Fixes Bas",
            lowFixedCostsDesc: "Vos coûts fixes (taxes, assurance) sont bas.",
            usage: "Utilisation Annuelle",
            usageDesc: "Distance parcourue au cours des 12 derniers mois.",
            accidentMinor: "Accident Mineur",
            accidentMinorDesc: "Dommages cosmétiques ou petites réparations.",
            structuralDamage: "Dommages Structurels",
            structuralDamageDesc: "Des problèmes de cadre ou structurels réduisent drastiquement la valeur.",
            aiLifespanNearEnd: "Fin de la Durée de Vie Attendue",
            aiLifespanAging: "Composants Vieillissants",
            aiLifespanGood: "Avantage de Faible Kilométrage",
            aiLifespanDesc: "Basé sur l'estimation de durée de vie typique par IA pour ce modèle.",
            aiUpcomingRepairs: "Réparations à Venir (Prédiction IA)",
            aiUpcomingRepairsDesc: "L'IA prédit des pannes majeures de composants bientôt en fonction du kilométrage.",
            ownershipFatigue: "Fatigue de possession",
            ownershipFatigueLong: "La possession à long terme conduit souvent à une préparation mentale pour un véhicule différent.",
            ownershipFatigueShort: "Le biais de possession initial crée une tendance plus forte à garder la voiture.",
            family_priority: "Famille & Longs Trajets",
            replacement_comparison: "Comparaison de Remplacement",
            none: "Aucun",
            surface: "Surface",
            minor: "Mineur",
            moderate: "Modéré",
            severe: "Sévère",
            structural: "Structurel",
            major: "Majeur",
            critical: "Critique",
            full: "Complet",
            improveLabel: "Ce qui pourrait améliorer cela :",
            info_original_price: "Le prix approximatif de cette voiture lorsqu'elle était neuve. Aide à estimer la stabilité de la valeur. Les voitures plus anciennes avec une valeur résiduelle plus faible sont financièrement plus sûres.",
            info_current_value: "Combien vous pourriez la vendre aujourd'hui. Utilisé pour vérifier si les réparations représentent un pourcentage important de la valeur de la voiture, ce qui signale un mauvais investissement.",
            info_yearly_mileage: "Un kilométrage plus élevé augmente l'usure. Un faible kilométrage peut prolonger considérablement la durée de vie de la voiture.",
            info_estimated_repairs: "Coût continu pour garder la voiture en état de marche. Des coûts annuels élevés réduisent l'attractivité de garder la voiture.",
            info_planned_repairs: "Réparations à venir connues. Simule une pression immédiate sur votre décision.",
            info_ownership_years: "Depuis combien de temps possédez-vous cette voiture ? Cela affecte la fatigue psychologique de possession et le biais d'acheteur initial.",
            info_rust: "Risque critique à long terme. La rouille structurelle réduit la sécurité. Une rouille sévère rend la vente une meilleure option.",
            info_crashes: "Les accidents structurels affectent la sécurité et la fiabilité. Ils réduisent également considérablement la valeur de revente.",
            info_repaint: "Signale des dommages passés ou des réparations cosmétiques. Impact faible à moins que des problèmes plus profonds aient été cachés.",
            info_unresolved: "Coûts futurs cachés. Défauts connus dans l'électronique, le moteur ou l'entretien qui nécessitent une attention.",
            info_fit: "La voiture répond-elle à vos besoins (espace, confort) ? Un mauvais ajustement signifie que vous êtes plus susceptible de la remplacer.",
            info_joy: "La valeur émotionnelle compte. Pas de joie signifie que vous êtes mentalement prêt pour une nouvelle voiture.",
            info_worry: "Stress perçu de fiabilité. Une grande inquiétude est un signal fort pour remplacer la voiture.",
        },
        explanations: {
            repairFreq: "Votre voiture a eu {visits} visites de service au cours des 12 derniers mois. Des visites fréquentes indiquent une fiabilité inférieure et plus de tracas.",
            repairVsValue: "Au cours des 12 derniers mois, vos réparations ont coûté €{cost}, tandis que la valeur estimée de la voiture est de €{value}. Cela représente {ratio}% de la valeur totale de la voiture.",
            stableRepairsDetailYearly: "L'année dernière, vous avez dépensé €{cost1} en réparations, mais l'année précédente €{cost2}. Changement de {percent}% ({trend}).",
            stableRepairsDetailHalfYearly: "Au cours des 6 derniers mois, vous avez dépensé €{cost1}, les 6 mois précédents €{cost2}. Changement de {percent}% ({trend}).",
            mileage: "À {mileage} km, la voiture entre dans une zone de risque plus élevé pour des pannes majeures (transmission, composants moteur).",
            planned: "Vous avez €{cost} en réparations prévues, ce qui réduit immédiatement la sécurité financière de garder la voiture.",
            lowDep: "Les voitures évaluées autour de €{value} perdent très peu de valeur par an, contrebalançant des coûts de réparation plus élevés.",
            depRisk: "Votre voiture a {age} ans et a principalement terminé son cycle d'amortissement, ce qui signifie que vous ne perdez pas d'argent sur sa valeur qui diminue.",
            stable: "Vous avez dépensé €{costThisYear} cette année contre €{costLastYear} l'année dernière, ce qui représente une diminution de {percent}%. Des coûts prévisibles sont mathématiquement favorables.",
            costKm: "Vous avez parcouru {km} km l'année dernière. Les coûts d'utilisation étaient de €{costKm} par kilomètre, ce qui montre une excellente efficacité économique.",
            fixedCosts: "Vous avez payé €{costs} en coûts fixes annuels (assurance/taxe). Cette faible empreinte offre une tranquillité d'esprit supplémentaire.",
            usage: "Vous conduisez environ {km} km par an selon vos relevés.",
            practical_fit: "Vous avez évalué l'adaptation pratique à {score}/5, ajustant le score mathématique de {impact} points.",
            emotional_satisfaction: "Vous avez évalué votre satisfaction émotionnelle à {score}/5, ajustant le score de {impact} points.",
            reliability_anxiety: "Votre confiance en la fiabilité est de {score}/5, impactant le score de {impact} points.",
            rust: "Une rouille structurelle ou sous-carrosserie sévère signifie généralement des dangers pour la sécurité ou des scénarios de perte totale.",
            accident: "Des dommages précédents, selon leur gravité, réduisent intrinsèquement la valeur de revente et la sécurité du véhicule.",
            structural: "Les problèmes structurels rendent automatiquement la voiture financièrement dangereuse.",
            ai_lifespan: "À {current} km, la voiture a complété {ratio}% de sa durée de vie estimée par l'IA ({expect} km).",
            ai_repairs: "Les données de l'IA indiquent que {count} réparations majeures spécifiques au modèle ont tendance à se produire à ce kilométrage.",
            lifecycle_stage: "Que signifie la durée de vie ? {total} km – c'est le point prédit par l'IA où les réparations structurelles majeures (moteur, boîte de vitesses, etc.) commencent généralement pour ce modèle. Ce n'est pas la fin de la vie de la voiture – la voiture peut encore rouler, mais avec des coûts d'entretien beaucoup plus élevés. La durée de vie physique complète d'une voiture typique est de 500k à 1M km, mais elle devient économiquement non viable beaucoup plus tôt.",
        },
        ratios: {
            healthy: "Sain",
            monitor: "Surveiller",
            highRisk: "Risque élevé",
            stable: "Stable",
            warning: "Avertissement",
            financialStress: "Stress financier",
        },
        proFeatureTitle: "Fonctionnalité Pro : Mode Réparation",
        proFeatureDesc: "Débloquez le moteur de décision pour obtenir une analyse financière professionnelle de vos réparations de véhicule. Les utilisateurs gratuits voient une démonstration avec des données d'exemple.",
        viewDemo: "Voir la Démo en Direct",
        upgradeToUnlock: "Mettez à niveau vers PRO pour analyser votre voiture",
    },
    carTasks: {
        title: "Tâches de voiture",
        placeholderTitle: "Suivi de maintenance",
        placeholderDesc: "Calendrier de maintenance pour votre modèle de voiture.",
        demoTitle: "Tâches de maintenance de voiture",
        demoSubtitle: "Suivi intelligent pour votre véhicule",
        feature1Title: "Génération automatique de tâches",
        feature1Desc: "Génère automatiquement des tâches comme les changements d'huile, les remplacements de filtre et les vérifications de courroie de distribution en fonction du modèle de votre voiture.",
        feature2Title: "Intégration des dépenses",
        feature2Desc: "Analyse votre historique de service pour comprendre ce qui a déjà été fait et quand.",
        feature3Title: "Rappels intelligents",
        feature3Desc: "Recevez des notifications en fonction du temps et du kilométrage. Le suivi se réinitialise automatiquement lorsque vous marquez une tâche comme terminée.",
        getStarted: "Commencer",
        fakeDoorPopupTitle: "Retour d'information bienvenu",
        fakeDoorPopupMessage: "Nous développons actuellement des tâches de maintenance de voiture. Cette fonctionnalité vous permettra de suivre automatiquement les intervalles de service. Quelles tâches ou fonctionnalités spécifiques aimeriez-vous voir le plus ?",
        fakeDoorPopupPlaceholder: "Vos pensées...",
        noTasksYet: "Pas encore de tâches",
        deleteAllTitle: "Effacer la liste des tâches ?",
        deleteAllMessage: "Êtes-vous sûr de vouloir supprimer toutes les {count} tâches pour cette voiture ? Cette action ne peut pas être annulée.",
        deleteAllSuccess: "Toutes les tâches ont été supprimées avec succès.",
        sortPriority: "Priorité",
        sortType: "Type",
        sortDate: "Dernier",
        status: {
            overdue: "EN RETARD",
            pending: "EN ATTENTE",
            noData: "AUCUNE DONNÉE",
            upcoming: "À venir",
            nextIn: "Prochain dans {dist} {unit}",
            overdueBy: "{dist} {unit} en retard",
            afterDays: "Dans {days} jours",
            overdueDays: "{days} jours de retard",
        },
        form: {
            newTitle: "Nouvelle Tâche",
            actionType: "Inspecter ou Remplacer ?",
            intervalMonths: "Intervalle (Mois)",
            intervalDistance: "Intervalle (Distance)",
            diffFirstInterval: "Le premier intervalle diffère de l'intervalle de base",
            firstInterval: "Premier Intervalle",
            historyToggle: "Cette tâche a-t-elle déjà été effectuée pour cette voiture ?",
            historyToggleDesc: "Si NON, nous supposons qu'elle n'a jamais été effectuée depuis la production.",
            lastPerformedOptional: "Quand a-t-elle été effectuée pour la dernière fois ? (Optionnel)",
            deleteTitle: "Supprimer la Tâche",
            deleteConfirm: "Êtes-vous sûr de vouloir supprimer cette tâche ?",
            saveError: "Échec de l'enregistrement de la tâche",
            historyConfirmed: "Historique Confirmé",
            reminders: "Rappels",
            notifyMileage: "Prévenez-moi lorsque le kilométrage estimé est atteint",
            notifyDate: "Prévenez-moi lorsque la date est atteinte",
            nextMileageReminder: "Date estimée",
            nextDateReminder: "Date d'échéance",
            estimatedDate: "Date estimée",
            missingLastMileage: "Dernier kilométrage effectué manquant",
            missingLastDate: "Dernière date effectuée manquante",
            missingStartDate: "Impossible de déterminer la date de début",
            additionalOptions: "Options supplémentaires",
            reminderInfoMissing: "État informatif : les rappels sont désactivés jusqu'à ce que les dernières données effectuées soient fournies.",
        },
    },
    dashboard: {
        title: "Tableau de Bord",
        quickActions: "Actions Rapides",
        addExpense: "Ajouter une Dépense",
        editCar: "Modifier la Voiture",
        recentExpenses: "Dépenses",
        seeAll: "Voir Tout",
        recentGallery: "Fichiers",
        thisMonth: "Ce Mois",
        totalSpent: "Total Dépensé",
        avgPerMonth: "Moyenne/Mois",
        recentNotes: "Notes",
        recentParts: "Pièces",
        noRecentExpenses: "Aucune dépense récente",
        noRecentGallery: "Aucune photo récente",
        noRecentNotes: "Aucune note récente",
        noRecentParts: "Aucune pièce récente",
        createNote: "Créer Nouveau",
        createPart: "Créer Nouveau",
        createExpense: "Créer Nouveau",
        noFiles: "Aucun fichier ajouté pour le moment",
        needSomethingMore: "Besoin de quelque chose de plus ?",
        letUsKnow: "faites-le nous savoir !",
    },
    expenseTypes: {
        editTitle: "Modifier le Type de Dépense",
        newTitle: "Nouveau Type de Dépense",
        categoryName: "Nom de la Catégorie",
        placeholderName: "ex. Service",
        signatureColor: "Couleur de Signature",
        icon: "Icône de Catégorie",
        deleteConfirm: "Êtes-vous sûr de vouloir supprimer le type '{name}' ?",
    },
    spareParts: {
        title: "Pièces Détachées",
        connectedWithRecords: "Connecté avec les Dossiers",
        toBuy: "À Acheter",
        usedInExpenses: "Utilisé dans les Dépenses",
        noPartsToBuy: "Aucune pièce ajoutée à la liste d'achats",
        noPartsUsed: "Aucune pièce liée aux dépenses pour le moment",
        quantity: "Qté",
        partCode: "Code de Pièce",
        price: "Prix",
        link: "Lien",
        comment: "Commentaire",
        addPart: "Ajouter une Pièce Détachée",
        editPart: "Modifier la Pièce Détachée",
        openPart: "Ouvrir l'entrée de la Pièce",
        nameLabel: "Nom",
        manufacturerLabel: "Fabricant",
        priceLabel: "Prix ({currency})",
        imagesLabel: "Fichiers",
        linkedHistory: "Historique Lié",
        partManagedNote: "Cette pièce est gérée par une dépense",
        cannotDeleteNote: "Puisque cette pièce est liée à une dépense, elle ne peut pas être supprimée séparément. Supprimez ou modifiez la dépense pour gérer le cycle de vie de cette pièce.",
        partNamePlaceholder: "Nom de la pièce...",
        manufacturerPlaceholder: "Bosch, Brembo, etc.",
        additionalInfoPlaceholder: "Informations supplémentaires...",
        namePlaceholder: "ex. Plaquettes de Frein",
        codePlaceholder: "ex. 5Q0 698 151",
        linkPlaceholder: "https://...",
        vinPlaceholder: "WBADT...",
        savePart: "Enregistrer la Pièce",
        deletePart: "Supprimer la pièce détachée",
        deleteConfirm: "Êtes-vous sûr de vouloir supprimer cette pièce détachée ?",
        sparePartsLabel: "Pièces Détachées",
        partNumber: "Pièce #{number}",
        imagesLimitLabel: "Fichiers (jusqu'à 5)",
        optional: "(facultatif)",
        selectPart: "Sélectionner une Pièce Détachée",
        createNew: "Créer une nouvelle pièce détachée",
        searchPlaceholder: "Rechercher par nom ou code...",
        noResults: "Aucune pièce trouvée",
        noPartsYet: "Aucune pièce pour le moment. Créez la vôtre !",
        selectorDescription: "Trouvez et sélectionnez une pièce détachée existante, ou créez-en une nouvelle",
        alreadyAdded: "Cette pièce est déjà ajoutée",
        statistics: {
            avgPrice: "Moy.",
            lastPrice: "Dernier",
            usedTimes: "Utilisé {count} fois",
            totalPurchased: "Total : {quantity}x",
        },
        usage: {
            title: "Cet Achat",
            quantity: "Quantité",
            pricePerUnit: "Prix par unité",
            comment: "Commentaire",
            commentPlaceholder: "ex. Acheté chez AutoZone",
            total: "Total",
            transactionData: "Cette transaction",
        },
        masterData: "Infos principales sur la pièce",
        masterRecord: {
            title: "Détails de la Pièce (partagé)",
            editMaster: "Modifier l'Enregistrement Principal",
            warning: "Les modifications de l'enregistrement principal affectent toutes les utilisations",
        },
    },
    reminders: {
        title: "Rappels",
        reminder: "Rappel",
        noteReminder: "Rappel de note",
        partReminder: "Rappel de pièce",
        today: "Aujourd'hui",
        tomorrow: "Demain",
        yesterday: "Hier",
        inXDays: "dans {x} jours",
        xDaysAgo: "il y a {x} jours",
        inXMonths: "dans {x} mois",
        xMonthsAgo: "il y a {x} mois",
        active: "Actif",
        activeDesc: "Si nécessaire, vous pouvez désactiver ce rappel sans le supprimer",
        disabled: "Désactivé",
        yearly: "Annuel",
        markSeen: "Accuser réception",
        addReminder: "Nouveau rappel",
        editReminder: "Modifier le rappel",
        extraReminders: "Rappels supplémentaires",
        extraRemindersDesc: "Envoyer 3 rappels supplémentaires aux jours suivants (4 au total) si non arrêté.",
        remindDate: "Date",
        remindTime: "Heure",
        repeatYearly: "Répéter annuellement",
        repeatYearlyDesc: "Ce rappel se produira automatiquement chaque année à la même date/mois/jour/heure jusqu'à ce qu'il soit désactivé",
        standalone: "Rappels autonomes",
        embedded: "Rappels liés",
        noReminders: "Aucun rappel actif",
        deleteReminder: "Supprimer le rappel",
        nameLabel: "Nom",
        descriptionLabel: "Description",
        namePlaceholder: "Nom du rappel",
        descriptionPlaceholder: "Courte description ou note",
        simulateReminder: "Simuler le moment du rappel",
        simulateSuccess: "E-mail de simulation envoyé",
        timezone: "Fuseau horaire",
        nextReminder: "Prochain rappel",
        futureTimeError: "L'heure du rappel doit être dans le futur",
    },
    notes: {
        title: "Notes",
        emptyNote: "Note vide",
        noNotes: "Aucune note pour le moment",
        addNote: "Ajouter une Note",
        editNote: "Modifier la Note",
        viewNote: "Voir la Note",
        openNote: "Ouvrir l'entrée de la Note",
        titleLabel: "Titre",
        contentLabel: "Contenu",
        noteTitlePlaceholder: "Titre de la note...",
        noteContentPlaceholder: "Écrivez votre note ici...",
        saveNote: "Enregistrer la note",
        deleteNote: "Supprimer la note",
        publicNote: "Rendre la note publique",
        publicNoteDesc: "D'autres peuvent voir cette note via un lien partagé",
        deleteConfirm: "Êtes-vous sûr de vouloir supprimer cette note ?",
        enterTitleAlert: "Veuillez entrer un titre",
        saveFailed: "Échec de l'enregistrement de la note",
        thisNote: "Cette note",
        deleteFailed: "Échec de la suppression de la note",
        pinToTop: "Épingler en haut",
    },
    locations: {
        title: "Lieux",
        singleTitle: "Lieu",
        newLocation: "Nouveau lieu",
        emptyLocation: "Lieu vide",
        noLocations: "Pour les statistiques - vous pouvez ajouter le lieu où cette dépense automobile a eu lieu",
        addLocation: "Ajouter un lieu",
        editLocation: "Modifier le lieu",
        viewLocation: "Voir le lieu",
        openLocation: "Ouvrir le lieu",
        createNew: "Créer un nouveau lieu",
        selectLocation: "Sélectionner un lieu",
        searchLocations: "Rechercher des lieux...",
        nameLabel: "Nom",
        addressLabel: "Adresse ou Coordonnées",
        descriptionLabel: "Commentaire/Description",
        namePlaceholder: "ex. : Service du centre-ville",
        mapLabel: "Lieu",
        searchPlaceholder: "Rechercher une adresse...",
        addressPlaceholder: "Entrez l'adresse et sélectionnez...",
        descriptionPlaceholder: "Informations supplémentaires...",
        fillAddress: "Remplir l'adresse à partir de la carte",
        saveLocation: "Enregistrer le lieu",
        deleteLocation: "Supprimer le lieu",
        deleteConfirm: "Êtes-vous sûr de vouloir supprimer ce lieu ?",
        enterNameAlert: "Veuillez entrer un nom",
        saveFailed: "Échec de l'enregistrement du lieu",
        thisLocation: "Ce lieu",
        deleteFailed: "Échec de la suppression du lieu",
        myLocations: "Mes lieux",
        usedInEntries: "Utilisé dans les entrées",
        noEntries: "Le lieu n'est pas utilisé",
        noEntriesThisCar: "Ce lieu n'a pas été utilisé pour cette voiture",
        removeLocation: "Supprimer le lieu",
        noResults: "Aucun résultat trouvé",
    },
    gallery: {
        title: "Fichiers",
        noImages: "Aucune image ajoutée",
        addImagesNote: "Ajouter des images à la voiture ou aux dépenses",
        totalImages: "Total de {count} images",
        imageAlt: "Image {number}",
        commentPlaceholder: "Commentaire sur le fichier",
        isReceipt: "Ce fichier est un reçu",
        deleteImageTitle: "Supprimer le fichier",
        deleteImageConfirm: "Êtes-vous sûr de vouloir supprimer ce fichier de façon permanente ?",
    },
    search: {
        placeholder: "Rechercher des dépenses, pièces, notes...",
        typeToSearch: "Tapez pour commencer la recherche",
        noResults: "Aucun résultat trouvé pour '{query}'",
        carImage: "Image de la voiture",
        expenseImage: "Image de la dépense",
        noteImage: "Image de la note",
        galleryImage: "Image de la galerie",
        untitledExpense: "Dépense sans titre",
    },
    landing: {
        nav: {
            features: "Fonctionnalités",
            howItWorks: "Comment ça marche",
            pricing: "Tarifs",
            login: "Connexion",
            getStarted: "Commencer",
            garage: "Garage",
        },
        hero: {
            title: "Sachez exactement ce que votre voiture",
            subtitle: "coûte vraiment",
            description: "Suivez les dépenses, enregistrez les réparations et voyez où va vraiment votre argent.",
            subheadline: "Le tableau de bord professionnel basé sur les données pour les propriétaires de voitures méticuleux.",
            startTracking: "Commencer",
            viewDemo: "Démo Voiture ->",
            trustSignal: "Rejoignez plus de 1 200 propriétaires méticuleux qui ont déjà cartographié plus de 5 millions de kilomètres de coûts réels de voiture.",
            ctaNote: "Pas de carte de crédit. Pas de ventes insistantes. Juste de la clarté.",
            benefits: {
                repairVsSell: {
                    title: `Réparer vs. Vendre`,
                    description: `Repérez le seuil "Réparer vs. Vendre" — Ne vous demandez plus si vous jetez de l'argent dans un gouffre financier. Sachez exactement à quel moment une réparation n'est plus un investissement judicieux.`
                },
                costPerKm: {
                    title: `Coût par KM`,
                    description: `Suivez votre coût réel par kilomètre — Allez au-delà du tableau de bord. Voyez l'ensemble de la situation financière de vos habitudes de conduite que d'autres applications omettent.`
                },
                automation: {
                    title: `Automatisation`,
                    description: `Automatisez votre tenue de dossiers — Il vous suffit de transférer vos factures ou d'envoyer simplement vos notes par e-mail. Notre IA organise l'historique technique de votre voiture pour vous.`
                }
            },
        },
        features: {
            title: "Tout ce dont vous avez besoin pour gérer votre voiture",
            subtitle: "Arrêtez d'utiliser des tableurs et des dossiers en désordre. My Car Costs apporte des outils professionnels de gestion de flotte à votre garage personnel.",
            expenseTracking: {
                title: "Suivi des dépenses",
                description: "Enregistrez le carburant, le service, l'assurance et les réparations. Catégorisez les coûts et visualisez les tendances de dépenses au fil du temps.",
            },
            spareParts: {
                title: "Gestion des pièces de rechange",
                description: "Tenez un inventaire détaillé des pièces de rechange, des numéros de pièces et des notes de compatibilité pour toutes vos réparations.",
            },
            serviceHistory: {
                title: "Historique des services et notes",
                description: "Maintenez un carnet de service numérique. Ajoutez des notes détaillées sur l'entretien, les modifications ou les projets futurs.",
            },
            documentStorage: {
                title: "Stockage de documents",
                description: "Téléchargez des reçus, des factures et des photos de votre voiture. Gardez toute votre documentation en sécurité et accessible.",
            },
            emailForwarding: {
                title: "Transfert d'e-mails",
                description: "Transférez les factures directement vers votre boîte de réception unique. Nous extrairons les données et les ajouterons automatiquement à votre tableau de bord.",
            },
            multiCar: {
                title: "Garage multi-voitures",
                description: "Gérez une voiture ou toute une flotte. Passez instantanément d'un véhicule à l'autre et voyez des statistiques agrégées.",
            },
            repeatingExpenses: {
                title: "Dépenses récurrentes",
                description: "Automatisez le suivi des coûts mensuels réguliers comme l'assurance, le stationnement ou la location.",
            },
            smartReminders: {
                title: "Rappels intelligents",
                description: "Définissez des alertes personnalisées pour les pièces de rechange et les notes de service afin de ne jamais manquer une tâche d'entretien.",
            },
        },
        howItWorks: {
            title: "Flux de travail simple et puissant",
            step1: {
                title: "Ajoutez votre véhicule",
                description: "Entrez les détails de votre voiture pour créer votre garage numérique.",
            },
            step2: {
                title: "Enregistrez les dépenses et les fichiers",
                description: "Prenez une photo d'un reçu ou transférez un e-mail. Nous nous occupons du reste.",
            },
            step3: {
                title: "Obtenez des informations",
                description: "Voyez votre coût par kilomètre, vos dépenses mensuelles et votre calendrier d'entretien.",
            },
        },
        useCases: {
            title: "Parfait pour",
            enthusiasts: {
                title: "Passionnés de voitures",
                description: "Suivez chaque modification, numéro de pièce et intervalle d'entretien pour votre voiture de projet.",
            },
            dailyDrivers: {
                title: "Conducteurs quotidiens",
                description: "Sachez exactement combien coûte votre trajet et ne manquez jamais une date de service.",
            },
            flippers: {
                title: "Le Flipper Intelligent",
                description: "Construisez un volume de données vérifiable qui prouve la vraie valeur de votre voiture aux acheteurs.",
            },
            fleets: {
                title: "Petites flottes",
                description: "Gérez plusieurs véhicules d'entreprise en un seul endroit sans le poids des logiciels d'entreprise.",
            },
        },
        foundersNote: {
            title: "Note du Fondateur",
            content: "Je construis MyCarCosts pour être l'outil ultime pour les personnes qui se soucient réellement de leurs voitures. Y a-t-il une fonctionnalité manquante dans votre flux de travail ? Contactez-moi directement. Si cela a du sens pour la communauté, nous le construirons ensemble.",
        },
        pricing: {
            freeLimitedTime: "Gratuit pour une durée limitée",
            title: "Tarification simple et transparente",
            subtitle: "Commencez gratuitement, améliorez à mesure que vous grandissez.",
            perMonth: "mois",
            monthly: "Mensuel",
            yearly: "Annuel",
            save25: "Économisez 25%",
            billedAnnually: "Facturé annuellement ({amount})",
            free: {
                title: "Gratuit",
                cta: "Commencer",
            },
            pro: {
                title: "Pro",
                cta: "Passer à Pro",
                recommended: "Recommandé",
                earlyAdopter: "Tarification pour les premiers utilisateurs",
                priceLock: "Verrouillez ce prix maintenant. Le prix peut augmenter à l'avenir.",
            },
            custom: {
                title: "Custom",
                cta: "Contactez-nous",
            },
            features: {
                oneCar: "1 voiture",
                unlimitedExpenses: "Dépenses illimitées",
                unlimitedSpareParts: "Pièces de rechange illimitées",
                unlimitedNotes: "Notes illimitées",
                repeatedExpenses: "Dépenses répétées",
                emailReminders: "Rappels par e-mail pour les notes et les pièces de rechange",
                basicStatistics: "Statistiques de base",
                fullStatistics: "Statistiques complètes",
                storage50mb: "Stockage de fichiers de 50 Mo",
                unlimitedArchivedCars: "Véhicules archivés illimités",
                dataExport: "Exportation de données",
                allInFreePlus: "tout inclus gratuit plus :",
                threeCars: "Jusqu'à 3 voitures",
                storage2gb: "Stockage de fichiers de 2 Go",
                emailParsing: "Analyse des e-mails",
                carAdmins: "Administrateurs de voiture",
                moreCars: "Plus de voitures",
                moreFileStorage: "Plus de stockage de fichiers",
                customRequirements: "Exigences personnalisées",
            },
            upgradePopup: {
                title: "Bientôt disponible !",
                message: "Les versions pro et personnalisées ne sont pas encore lancées, mais elles le seront bientôt. Pour nous aider à lancer plus rapidement - veuillez noter quelle fonctionnalité spécifique vous attendez de la version pro / personnalisée, ou toute autre chose que vous aimeriez que nous améliorions pour rendre cette plateforme encore meilleure pour vous !",
                placeholder: "Écrivez vos commentaires ici...",
                submit: "Envoyer",
                cancel: "Annuler",
                thanksTitle: "Merci !",
                thanksMessage: "Nous avons reçu vos commentaires et nous travaillerons pour rendre MyCarCosts encore meilleur !",
                thanksOk: "D'accord",
            },
        },
        migration: {
            title: "Passer d'Excel ou d'une autre application ?",
            subtitle: "Nous offrons un service de migration de données professionnel pour vous faire fonctionner instantanément.",
            pricingModel: "Modèle de tarification",
            small: {
                title: "Petit ensemble de données",
                description: "< 500 lignes",
                price: "$19–29",
            },
            medium: {
                title: "Ensemble de données moyen",
                description: "500–5 000 lignes",
                price: "$49",
            },
            large: {
                title: "Grand / Désordonné",
                description: "Systèmes hérités ou fichiers complexes",
                price: "Devis personnalisé",
            },
            process: {
                title: "Comment ça fonctionne",
                step1: "Vous soumettez votre fichier",
                step2: "Nous évaluons les données",
                step3: "Paiement unique",
                step4: "Nous gérons l'importation",
            },
            cta: "Demander la migration",
        },
        repairAnalysisSection: {
            title: "Réparer ou vendre ? Laissez les données décider.",
            subtitle: "Analyse financière professionnelle pour votre véhicule. Arrêtez de deviner et commencez à savoir si ces réparations en valent vraiment la peine.",
            features: {
                personalized: {
                    title: "Analyse personnalisée",
                    description: "Pour vos voitures suivies, nous utilisons de réelles dépenses historiques et des paramètres, ajoutant des coûts prévus pour générer une évaluation.",
                },
                public: {
                    title: "Simulateur public",
                    description: "Évaluez un achat potentiel ? Utilisez notre outil public à /repair pour générer une analyse détaillée en entrant juste quelques détails clés.",
                },
                whatIf: {
                    title: "Simulateur What-If",
                    description: "Entrez un coût de réparation prévu et voyez instantanément comment cela affecte le score financier de votre voiture.",
                },
            },
            cta: "Voir l'analyse d'exemple",
        },
        faq: {
            title: "Questions Fréquemment Posées",
            subtitle: "Tout ce que vous devez savoir sur My Car Costs",
            questions: {
                mobileApps: {
                    question: "Y aura-t-il des applications iOS et Android ?",
                    answer: "Oui ! Des applications mobiles pour iOS et Android arrivent bientôt. Nous travaillons dur pour vous offrir la meilleure expérience mobile.",
                },
                dataPrivacy: {
                    question: "Mes données sont-elles privées et sécurisées ?",
                    answer: "Absolument. Vos données sont cryptées et stockées en toute sécurité. Vous êtes le seul à pouvoir accéder à vos informations de voiture, sauf si vous choisissez explicitement de les rendre publiques.",
                },
                freePlan: {
                    question: "Le plan gratuit est-il vraiment gratuit pour toujours ?",
                    answer: "Oui ! Le plan gratuit sera toujours disponible avec une voiture et toutes les fonctionnalités essentielles. Vous pouvez passer à Pro à tout moment pour jusqu'à 3 voitures et des fonctionnalités avancées.",
                },
                dataExport: {
                    question: "Puis-je exporter mes données ?",
                    answer: "Oui, vous pouvez exporter toutes vos données à tout moment au format CSV ou PDF. Vos données vous appartiennent, et vous n'êtes jamais bloqué.",
                },
                emailImport: {
                    question: "Comment fonctionne l'importation d'e-mails ?",
                    answer: "Chaque voiture obtient une adresse e-mail unique. Il vous suffit de transférer vos factures et nous extrairons les données automatiquement.",
                },
                multipleUsers: {
                    question: "Plusieurs personnes peuvent-elles accéder à la même voiture ?",
                    answer: "Actuellement, chaque voiture appartient à un seul compte. Cependant, vous pouvez rendre une voiture visible publiquement pour partager un accès en lecture seule avec d'autres.",
                },
            },
        },
        cta: {
            title: "Prêt à prendre le contrôle ?",
            subtitle: "Rejoignez des milliers de propriétaires de voitures qui font confiance à My Car Costs pour garder leurs véhicules en bon état et leurs finances sous contrôle.",
            button: "Commencer maintenant",
            note: "Pas de carte de crédit. Pas de ventes insistantes. Juste de la clarté.",
        },
        footer: {
            product: "Produit",
            legal: "Légal",
            changelog: "Journal des modifications",
            privacy: "Politique de confidentialité",
            contact: "Contact",
            contactEmail: "info@mycarcosts.com",
            questions: "Vous avez des questions ? Contactez-nous à :",
        },
        privacy: {
            title: "Politique de confidentialité",
            lastUpdated: "Dernière mise à jour : 25 mars 2026",
            introduction: "Chez My Car Costs, nous prenons votre vie privée au sérieux. Cette politique explique comment nous gérons vos données conformément aux normes de confidentialité modernes, y compris le RGPD.",
            dataCollection: {
                title: "Collecte de données",
                description: "Nous ne collectons que le strict minimum d'informations nécessaires à la fourniture de notre service.",
                email: "Identité du compte : Nous utilisons votre adresse e-mail comme identifiant unique et pour l'authentification (par ex. liens magiques ou connexion Google).",
                documents: "Stockage de documents : Les fichiers que vous téléchargez sont stockés en toute sécurité dans un stockage cloud crypté.",
            },
            carData: {
                title: "VOTRE contrôle sur les données",
                description: "Les informations que vous saisissez (dépenses, kilométrage, pièces) sont stockées pour votre usage personnel et vos analyses.",
                noValidation: "Nous ne validons pas les données du véhicule que vous fournissez ; il vous appartient d'en assurer l'exactitude.",
                privateByDefault: "Par défaut, vos données sont privées. Les identifiants techniques des fichiers sont non séquentiels et ne sont pas indexés par les moteurs de recherche.",
                rightToForgotten: "Droit à l'Oubli : Vous pouvez supprimer votre compte à tout moment, ce qui supprime immédiatement et définitivement tous vos enregistrements et fichiers.",
            },
            thirdParty: {
                title: "Services tiers",
                description: "Nous ne vendons pas vos données personnelles. Lors de l'utilisation de la connexion Google, nous accédons uniquement à votre adresse e-mail à des fins d'identification.",
            },
            emailIngestion: {
                title: "Collecte d'e-mails",
                description: "Les e-mails envoyés à notre service de collecte sont stockés pour fournir un historique de traitement. Vous pouvez supprimer manuellement des e-mails individuels ou tout votre historique à tout moment.",
            },
        },
        emails: {
            deleteAll: "Tout supprimer",
            deleteAllConfirm: "Êtes-vous sûr de vouloir supprimer tout l'historique des e-mails pour cette voiture ? Cette action est irréversible.",
            deleteConfirm: "Êtes-vous sûr de vouloir supprimer cet e-mail ?",
            noEmails: "Aucun e-mail reçu pour le moment.",
        },
        terms: {
            title: "Conditions Générales",
            lastUpdated: "Dernière mise à jour : 25 mars 2026",
            introduction: "En accédant à MyCarCosts ou en l'utilisant, vous acceptez d'être lié par ces Conditions Générales. Si vous n'êtes pas d'accord, veuillez ne pas utiliser notre service.",
            acceptance: {
                title: "1. Acceptation des Conditions",
                description: "Ces conditions constituent un accord juridiquement contraignant entre vous et MyCarCosts concernant l'utilisation de notre application et de nos services.",
            },
            service: {
                title: "2. Description du Service",
                description: "MyCarCosts propose une plateforme de suivi des dépenses de véhicules, de gestion de l'entretien et d'analyse financière. Cela inclut l'analyse des reçus par IA et des outils de visualisation de données.",
            },
            responsibilities: {
                title: "3. Responsabilités de l'Utilisateur",
                description: "Vous êtes responsable de l'exactitude de toutes les données que vous saisissez, y compris le kilométrage, les numéros VIN et les coûts. Vous devez sécuriser vos identifiants de connexion (e-mail/lien magique) et ne pas les partager avec des tiers non autorisés.",
            },
            plans: {
                title: "4. Forfaits et Limites",
                description: "Nous proposons des forfaits Gratuit et Pro avec différents types de stockage, de nombre de voitures et de limites d'envoi. Nous nous réservons le droit de modifier ces limites ou d'introduire de nouveaux niveaux d'abonnement à tout moment.",
            },
            disclaimer: {
                title: "5. Clauses de Non-Responsabilité et Exactitude",
                description: "MyCarCosts est un outil d'information et d'organisation. Nous ne fournissons pas de certifications officielles de santé du véhicule. Les données analysées par l'IA à partir des reçus doivent toujours être vérifiées par l'utilisateur pour en assurer l'exactitude.",
            },
            liability: {
                title: "6. Limitation de Responsabilité",
                description: "MyCarCosts est fourni 'en l'état'. Nous ne sommes pas responsables des problèmes mécaniques, des pertes financières ou des inexactitudes de données résultant de l'utilisation de notre application.",
            },
            termination: {
                title: "7. Résiliation",
                description: "Vous pouvez cesser d'utiliser le service et supprimer vos données à tout moment. Nous nous réservons le droit de suspendre ou de fermer les comptes qui enfreignent ces conditions ou ont un comportement abusif.",
            },
        },
    },
            audit: {
        hero: {
            badge: 'New: Protection Tool',
            title: 'Consult the Oracle.',
            subtitle: 'Stop second-guessing your mechanic. Paste your quote, describe the noise, or upload a photo. Get a direct, logic-based verdict in 60 seconds.',
            cta: 'Consult the Oracle',
            smallPrint: '100% Private. No credit card required.',
            walletSecurity: 'AI-Powered Security for Your Wallet'
        },
        demo: {
            title: 'Audit Results',
            verdictSell: 'Verdict: SELL',
            repairVsValue: 'Repair vs Value',
            cashGapSavings: 'Cash Gap to Replace',
            savingsValue: '$3,200 Savings',
            roiStrategyTitle: 'ROI Strategy',
            roiStrategyText: 'Investing $2,100 into a deteriorating $4,500 asset is a negative ROI. Replacing with a 2018 model now saves you $3,200 in long-term equity.',
            benefitsTitle: 'Stop Guessing. Master Your Car\'s ROI.',
            ctaSubtext: 'Stop guessing. Put your car\'s costs into logic-driven perspective.'
        },
        form: {
            uploadTitle: 'Photos or Documents (Optional)',
            uploadDesc: 'Upload a photo of your quote or just a picture of the problem area.',
            emailLabel: 'Email Address',
            emailPlaceholder: 'Where to send your report?',
            carLabel: 'Car Model',
            carPlaceholder: 'e.g. 2018 BMW X5 TDI 2.0 sedan',
            descLabel: 'Describe the problem',
            descPlaceholder: 'Paste your mechanic quote, describe the noise, or tell us the problem...',
            submit: 'Consult the Oracle',
            submitting: 'Processing...',
            successTitle: 'Quote Received!',
            successDesc: 'Our AI agents are analyzing your data. You will receive a detailed report at {email} within 60 seconds.',
            errorNoFiles: 'Please provide a description or upload a file.',
            errorNoEmail: 'Please enter your email address.',
            verifiedBy: 'Verified by Double-Agent Protocol'
        },
        progress: {
            analyzing: 'Analysis in progress...',
            scanning: 'Scanning Documents',
            checking: 'Checking Market Data',
            roi: 'ROI Calculations',
            finalizing: 'Finalizing Report',
            lightningTitle: 'First Look Verdict',
            lightningBadge: 'LIGHTNING ANALYSIS',
            firstImpression: 'First Impression',
            checkEmail: 'Check your email in 60 seconds',
            anotherAudit: 'Audit Another Situation',
            statusDesc: 'Our Double-Agent system is scrutinizing your data. The final report will be sent to your email.'
        },
        valueProps: {
            negotiation: {
                title: 'ROI Analysis',
                desc: 'Stop overpaying for labor. Get data-backed arguments for real repair times.'
            },
            peace: {
                title: 'Verdict in 60s',
                desc: 'Know for sure if your mechanic is honest about repairs or trying to "bloat" the bill.'
            },
            scams: {
                title: 'Avoid Common Scams',
                desc: 'Our database found that the average quote in big city dealerships is padded by 22% for "consultation" fees that aren\'t necessary.'
            },
            technical: {
                title: 'Replacement Logic',
                desc: 'We calculate the "Cash Gap" — the true cost of fixing your car versus replacing it with something reliable.'
            }
        },
        cta: {
            title: 'Stop dumping cash into a car that isn’t worth it.',
            button: 'Audit the Situation Now'
        },
        howItWorks: {
            title: 'How It Works',
            subtitle: 'The "Double-Agent" verification system ensures you get facts, not fluff.',
            extract: {
                title: 'Data Intake',
                desc: 'We analyze your situation and car model, factoring in mileage and historical maintenance trends.'
            },
            analyze: {
                title: 'Market Analysis',
                desc: 'Our AI compares repair costs against current market valuations and model reliability scores.'
            },
            audit: {
                title: 'ROI Calculation',
                desc: 'We calculate the financial gap between fixing your current vehicle and moving to a better one.'
            },
            verdict: {
                title: 'The Verdict',
                desc: 'You receive a clear "Repair or Sell" report with logic-driven strategy and priority action items.'
            }
        },
        results: {
            priceRealityCheck: 'Price Reality Check',
            roadmapTitle: 'What breaks next?',
            roadmapBadge: 'Roadmap Teaser',
            unlockFullReport: 'Unlock Full Forensic Audit',
            realWorldInsights: 'Real-World Insights',
            oracleAdvice: 'Real Advice from the Oracle',
            oracleAdviceSub: 'See how our AI helps real people navigate complex car problems every day.',
            readFullAudit: 'Read Full Audit',
            potentialSaving: 'Potential Saving',
            getYourOwn: 'Get your own Oracle Audit',
            savingsPotentialFound: 'Savings Potential Found',
            negotiationScript: 'Negotiation Script'
        },
        caseStudy: {
            backToAudit: 'Back to Audit',
            oracleCaseStudy: 'The Oracle Case Study',
            realUserQuestion: 'The Real User Question',
            officialAudit: 'Official Oracle Audit',
            fullReport: 'Full Resolution Report',
            economicImpact: 'Economic Impact',
            startYourAudit: 'Start Your Own Audit',
            moreAudits: 'More Real-World Audits',
            sharingIsCaring: "Sharing is caring. Save someone else's wallet.",
            poweredBy: 'Powered by MyCarCosts Double-Agent Intelligence'
        }
    },
    jarvis: {
        title: 'Jarvis',
        subtitle: 'AI Garage Agent',
        askJarvis: 'Ask Jarvis...',
        typing: 'Jarvis is thinking...',
        safetyWarning: 'Safety Warning',
        repairVerdict: 'Repair Verdict',
        diyFeasibility: 'DIY Feasibility'
    },
    repairMode: {
        oracleStatus: 'Oracle Status: {status}',
        verdictKeep: 'KEEP',
        verdictSell: 'SELL',
        verdictBorderline: 'BORDERLINE'
    }
};

